import React, { useState, useMemo, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MyContext } from "../../../../Context/MyContext";
import { toast } from "react-toastify";
import { FaEdit, FaTrash, FaEye } from "react-icons/fa";
import ImageWithLoader from "../../../../Config/ImageWithLoader";
import { getProfileDraftKeys } from "../../../../utils/draftKeys";

const BuildingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" width="52" height="52">
    <rect x="2" y="7" width="20" height="14" rx="2"/>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
  </svg>
);
const ProjectIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" width="52" height="52">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const AllIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" width="52" height="52">
    <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
    <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const TABS = [
  { key: "all",           label: "All Listings", Icon: AllIcon      },
  { key: "my_properties", label: "Properties",   Icon: BuildingIcon },
  { key: "my_projects",   label: "Projects",     Icon: ProjectIcon  },
  { key: "drafts",        label: "Drafts",       Icon: AllIcon      },
];

const PROFILE_PAGE_SIZE = 12;

/** Fetch every page for a user so counts stay accurate beyond the API page size. */
async function fetchAllPages(fetchPropertiesPaged, baseParams) {
  const limit = 100;
  let page = 1;
  let pages = 1;
  const all = [];
  do {
    const res = await fetchPropertiesPaged({ ...baseParams, page, limit });
    const chunk = Array.isArray(res?.data) ? res.data : [];
    all.push(...chunk);
    pages = Math.max(1, Number(res?.pages) || 1);
    if (!chunk.length) break;
    page += 1;
  } while (page <= pages);
  return all;
}

const ProfileCard = ({ property, isDraft, onDeleted }) => {
  const { categorys, cities, states, propertyTypes, deleteProperty, imgUrl } = useContext(MyContext);
  const navigate = useNavigate();

  const category = categorys?.find((c) => Number(c.id) === Number(property.category_id));
  const city = cities?.find((c) => Number(c.id) === Number(property.city_id));
  const state = states?.find((s) => Number(s.id) === Number(property.state_id || city?.state_id));
  const propType = propertyTypes?.find((t) => Number(t.id) === Number(property.type_id));

  const handleEdit = () => {
    if (property.is_project) navigate(`/project/create?editId=${property.id}`);
    else navigate(`/property/create?editId=${property.id}`);
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure?")) return;
    if (isDraft && !property._fromBackend) {
      const keys = getProfileDraftKeys();
      const DRAFT_KEY = property.is_project ? keys.project : keys.property;
      try {
        const stored = JSON.parse(localStorage.getItem(DRAFT_KEY)) || [];
        const filtered = Array.isArray(stored)
          ? stored.filter(d => String(d.id) !== String(property.id))
          : [];
        localStorage.setItem(DRAFT_KEY, JSON.stringify(filtered));
      } catch {}
      onDeleted?.();
    } else {
      try {
        await deleteProperty(property.id);
        toast.success("Deleted successfully");
        onDeleted?.();
      } catch { toast.error("Delete failed"); }
    }
  };

  const formatSingle = (p) => {
    if (!p) return "";
    if (p >= 10000000) return `₹${(p / 10000000).toFixed(2)} Cr`;
    if (p >= 100000)   return `₹${(p / 100000).toFixed(2)} L`;
    return `₹${Number(p).toLocaleString("en-IN")}`;
  };

  const formatPrice = () => {
    if (property.is_project && (property.price_from || property.price_to)) {
      const a = property.price_from ? formatSingle(property.price_from) : null;
      const b = property.price_to ? formatSingle(property.price_to) : null;
      if (a && b) return `${a} – ${b}`;
      return a || b || "Price on request";
    }
    if (!property.price) return "Price on request";
    return formatSingle(property.price);
  };

  const propertyTypeLabel = propType?.type_name
    || (property.is_project ? "Project" : "Property");
  const locationLabel = [
    property.map_location_name,
    property.address,
    property.location,
  ].map((v) => String(v || '').trim()).find(Boolean) || "—";

  return (
    <div className="listing-card listing-card--simple">
      <div className="listing-image">
        <ImageWithLoader
          src={
            property.thumbnail
              ? (imgUrl ? imgUrl(property.thumbnail) : property.thumbnail)
              : "/placeholder.jpg"
          }
          alt=""
          onError={e => { e.target.src = "/placeholder.jpg"; }}
        />
      </div>
      <div className="listing-content">
        <div className="listing-top">
          <h3>{category?.category_name || "Listing"}</h3>
          <span className="price-tag">{formatPrice()}</span>
        </div>
        <div className="listing-meta-grid">
          <div className="listing-meta-row">
            <span className="listing-meta-label">Type</span>
            <span className="listing-meta-value">{propertyTypeLabel}</span>
          </div>
          <div className="listing-meta-row">
            <span className="listing-meta-label">Category</span>
            <span className="listing-meta-value">{category?.category_name || "—"}</span>
          </div>
          <div className="listing-meta-row">
            <span className="listing-meta-label">Location</span>
            <span className="listing-meta-value" title={locationLabel}>{locationLabel}</span>
          </div>
          <div className="listing-meta-row">
            <span className="listing-meta-label">State</span>
            <span className="listing-meta-value">{state?.state_name || "—"}</span>
          </div>
          <div className="listing-meta-row">
            <span className="listing-meta-label">City</span>
            <span className="listing-meta-value">{city?.city_name || "—"}</span>
          </div>
        </div>
        <div className="listing-actions">
          <button type="button" onClick={handleEdit} title="Edit" aria-label="Edit"><FaEdit/></button>
          <button type="button" onClick={handleDelete} title="Delete" aria-label="Delete"><FaTrash/></button>
          {!isDraft && (
            <button
              type="button"
              onClick={() => navigate(`/property/${property.id}`)}
              title="View"
              aria-label="View"
            >
              <FaEye/>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Loading skeleton ────────────────────────────────────────────
const CardSkeleton = () => (
  <div className="listing-card" style={{ opacity: 0.6 }}>
    <div className="listing-image" style={{ background: '#e5e7eb', minHeight: 120 }}/>
    <div className="listing-content">
      <div style={{ height: 16, background: '#e5e7eb', borderRadius: 4, marginBottom: 8, width: '60%' }}/>
      <div style={{ height: 12, background: '#e5e7eb', borderRadius: 4, width: '40%' }}/>
    </div>
  </div>
);

// ── Main component ──────────────────────────────────────────────
const ProfileContent = ({ activeTab, setActiveTab }) => {
  const { user, deleteProperty, fetchPropertiesPaged } = useContext(MyContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [myProperties, setMyProperties] = React.useState([]);
  const [loadingProps, setLoadingProps]  = React.useState(false);
  const [fetchError,   setFetchError]    = React.useState(null);
  const [statusFilter, setStatusFilter]  = React.useState("all");
  const [listPage, setListPage]          = React.useState(1);
  const [, forceUpdate] = React.useState(0);

  const userId = user?.id;

  // ── Fetch this user's properties directly from the backend ────
  // Both live listings and backend-saved drafts are fetched in parallel.
  // Pages are walked until complete so counts stay accurate past 100.
  const loadUserProperties = React.useCallback(async () => {
    if (!userId) return;
    setLoadingProps(true);
    setFetchError(null);
    try {
      const [live, drafts] = await Promise.all([
        fetchAllPages(fetchPropertiesPaged, { user_id: userId, is_draft: false }),
        fetchAllPages(fetchPropertiesPaged, { user_id: userId, is_draft: true }),
      ]);
      // Never show Admin/Partner staff rows here (same-browser co-login can leak user_id)
      const isEndUserListing = (p) => {
        const r = String(p?.role || '').trim().toLowerCase();
        return !r || r === 'user' || r === 'owner' || r === 'agent';
      };
      setMyProperties([...live, ...drafts].filter(isEndUserListing));
    } catch (e) {
      console.error("ProfileContent: failed to fetch user properties:", e.message);
      setFetchError("Failed to load your properties. Please try again.");
    } finally {
      setLoadingProps(false);
    }
  }, [userId, fetchPropertiesPaged]);

  React.useEffect(() => {
    loadUserProperties();
  }, [loadUserProperties, location.key]);

  React.useEffect(() => {
    const onFocus = () => { loadUserProperties(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadUserProperties]);

  React.useEffect(() => { setStatusFilter("all"); setListPage(1); }, [activeTab]);
  React.useEffect(() => { setListPage(1); }, [statusFilter]);

  // localStorage drafts (offline / in-progress drafts not yet saved to backend)
  const storedDrafts = useMemo(() => {
    try {
      const keys = getProfileDraftKeys();
      const p = JSON.parse(localStorage.getItem(keys.property));
      const j = JSON.parse(localStorage.getItem(keys.project));
      const pArr = Array.isArray(p) ? p : p ? [p] : [];
      const jArr = Array.isArray(j) ? j : j ? [j] : [];
      return [...pArr, ...jArr].filter(
        d => d && (userId ? Number(d.user_id) === Number(userId) : true)
      );
    } catch { return []; }
  }, [userId, forceUpdate]); // eslint-disable-line

  const filteredData = useMemo(() => {
    if (!userId) return [];
    let data = [];

    if (activeTab === "drafts") {
      const backendDrafts = myProperties
        .filter(p => p.is_draft)
        .map(p => ({ ...p, _fromBackend: true }));
      const backendIds = new Set(backendDrafts.map(p => String(p.id)));
      const localOnly  = storedDrafts.filter(d => !backendIds.has(String(d.id)));
      data = [...backendDrafts, ...localOnly];
    } else if (activeTab === "my_properties") {
      data = myProperties.filter(p => !p.is_project && !p.is_draft);
    } else if (activeTab === "my_projects") {
      data = myProperties.filter(p => p.is_project  && !p.is_draft);
    } else {
      data = myProperties.filter(p => !p.is_draft);
    }

    if (activeTab !== "drafts" && statusFilter !== "all") {
      data = data.filter(p => String(p.status_id) === String(statusFilter));
    }
    return data;
  }, [activeTab, statusFilter, userId, myProperties, storedDrafts]);

  const counts = useMemo(() => {
    if (!userId) return { all:0, my_properties:0, my_projects:0, drafts:0 };
    const backendDraftCount = myProperties.filter(p => p.is_draft).length;
    return {
      all:           myProperties.filter(p => !p.is_draft).length,
      my_properties: myProperties.filter(p => !p.is_project && !p.is_draft).length,
      my_projects:   myProperties.filter(p => p.is_project  && !p.is_draft).length,
      drafts:        backendDraftCount + storedDrafts.length,
    };
  }, [userId, myProperties, storedDrafts]);

  const handleCreate = () => {
    const context = activeTab === "my_projects" ? "project" : "property";
    navigate(`/role?context=${context}`);
  };

  const isProject   = activeTab === "my_projects";
  const entityLabel = isProject ? "Project" : "Property";
  const EmptyIcon   = isProject ? ProjectIcon : activeTab === "all" ? AllIcon : BuildingIcon;

  const totalFiltered = filteredData.length;
  const totalListPages = Math.max(1, Math.ceil(totalFiltered / PROFILE_PAGE_SIZE));
  const safePage = Math.min(listPage, totalListPages);
  const pagedData = useMemo(() => {
    const start = (safePage - 1) * PROFILE_PAGE_SIZE;
    return filteredData.slice(start, start + PROFILE_PAGE_SIZE);
  }, [filteredData, safePage]);

  return (
    <div className="profile-content">

      {/* Tabs */}
      <div className="profile-tabs">
        {TABS.map(({ key, label }) => (
          <button key={key} className={activeTab === key ? "active" : ""} onClick={() => setActiveTab(key)}>
            {label}
            {counts[key] > 0 && (
              <span className={`tab-count ${activeTab === key ? "tab-count-active" : ""}`}>
                {counts[key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Header row */}
      <div className="content-header">
        <div className="header-left">
          {activeTab !== "drafts" && (
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="status-dropdown">
              <option value="all">All Status</option>
              <option value="1">Awaiting</option>
              <option value="2">Approved</option>
              <option value="3">Rejected</option>
            </select>
          )}
          {!loadingProps && totalFiltered > 0 && (
            <span className="profile-list-meta">
              {totalFiltered} listing{totalFiltered === 1 ? '' : 's'}
              {totalFiltered > PROFILE_PAGE_SIZE && (
                <> · page {safePage} of {totalListPages}</>
              )}
            </span>
          )}
        </div>
        <button className="create-btn-sm" onClick={handleCreate}>
          <PlusIcon/> Add {entityLabel}
        </button>
      </div>

      {/* Error */}
      {fetchError && (
        <div className="no-data-box" style={{ color: '#dc2626' }}>
          <p>{fetchError}</p>
          <button onClick={loadUserProperties} className="create-btn" style={{ marginTop: 12 }}>
            Retry
          </button>
        </div>
      )}

      {/* Loading skeleton */}
      {!fetchError && loadingProps && (
        <div className="profile-cards">
          {[1, 2, 3].map(i => <CardSkeleton key={i} />)}
        </div>
      )}

      {/* Cards / empty state */}
      {!fetchError && !loadingProps && (
        filteredData.length > 0 ? (
          <>
            <div className="profile-cards">
              {pagedData.map(item => (
                <ProfileCard
                  key={item.id || item._id}
                  property={item}
                  isDraft={activeTab === "drafts"}
                  onDeleted={() => { forceUpdate(n => n + 1); loadUserProperties(); }}
                />
              ))}
            </div>
            {totalListPages > 1 && (
              <div className="profile-pagination">
                <button
                  type="button"
                  className="profile-pg-btn"
                  disabled={safePage <= 1}
                  onClick={() => setListPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <span className="profile-pg-info">{safePage} / {totalListPages}</span>
                <button
                  type="button"
                  className="profile-pg-btn"
                  disabled={safePage >= totalListPages}
                  onClick={() => setListPage((p) => Math.min(totalListPages, p + 1))}
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="no-data-box">
            <div className="no-data-icon"><EmptyIcon/></div>
            <h3>No {activeTab === "all" ? "Listings" : `${entityLabel}s`} Yet</h3>
            <p>
              {activeTab === "all"
                ? "You haven't added any listings yet."
                : `You haven't listed any ${entityLabel.toLowerCase()}s yet.`}
            </p>
            <button onClick={handleCreate} className="create-btn">
              <PlusIcon/> Add {activeTab === "all" ? "Listing" : entityLabel}
            </button>
          </div>
        )
      )}

    </div>
  );
};

export default ProfileContent;